import React from "react";
import { useUser } from "@clerk/clerk-react";
import useApi from "../../../shared/hooks/useApi";
import Button from "../../../shared/components/FormElements/Button";
import LoadingSpinner from "../../../shared/components/UIElements/LoadingSpinner";
import { DocumentTextIcon } from "@heroicons/react/20/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/16/solid";
import classNames from "classnames";

const formatAmount = (cents, currency) => {
	try {
		return new Intl.NumberFormat("nl-NL", {
			style: "currency",
			currency: currency || "EUR",
		}).format((cents || 0) / 100);
	} catch (_) {
		return `${(cents || 0) / 100} ${currency || "EUR"}`;
	}
};

const formatDate = (unixSeconds) => {
	if (!unixSeconds) return "";
	const d = new Date(unixSeconds * 1000);
	return d.toLocaleDateString("nl-NL", {
		year: "numeric",
		month: "short",
		day: "2-digit",
	});
};

const statusToLabel = (status) => {
	switch ((status || "").toLowerCase()) {
		case "draft":
			return "Concept";
		case "open":
			return "Open";
		case "paid":
			return "Betaald";
		case "uncollectible":
			return "Oninbaar";
		case "void":
			return "Geannuleerd";
		default:
			return status || "";
	}
};

const statusToClasses = (status) => {
	switch ((status || "").toLowerCase()) {
		case "paid":
			return "bg-green-50 text-green-700 ring-green-700/30";
		case "open":
			return "bg-blue-50 text-blue-700 ring-blue-600/30";
		case "draft":
			return "bg-gray-50 text-gray-700 ring-gray-600/30";
		case "uncollectible":
			return "bg-red-50 text-red-700 ring-red-600/30";
		case "void":
			return "bg-amber-50 text-amber-700 ring-amber-600/30";
		default:
			return "bg-gray-50 text-gray-700 ring-gray-600/30";
	}
};

export default function InvoicesList() {
	const { user } = useUser();
	const { data, loading } = useApi(
		user?.id ? `/api/billing/settings/invoices/${user.id}` : null,
		{ refreshInterval: 60000 },
	);

	if (loading) {
		return (
			<LoadingSpinner />
		);
	}

	const invoices = data?.invoices || [];

	return (
		<div className="">
			<div className="flex items-center gap-2">
				<DocumentTextIcon className="size-5 text-brand-intense" />
				<h2 className="text-lg font-semibold">Facturen</h2>
			</div>
			{invoices.length === 0 ? (
				<p className="text-sm text-slate-700 mt-4">
					Geen facturen gevonden.
				</p>
			) : (
				<div className="mt-4 overflow-hidden rounded-2xl ring-1 ring-slate-200">
					<table className="min-w-full divide-y divide-slate-200">
						<thead className="bg-slate-50">
							<tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:text-xs [&>th]:font-medium [&>th]:text-slate-700 [&>th]:text-left">
								<th>Nummer</th>
								<th>Datum</th>
								<th>Status</th>
								{/* <th>Totaal</th> */}
								<th>Actie</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-slate-200">
							{invoices.map((inv) => (
								<tr key={inv.id} className="">
									<td className="px-4 py-3 text-sm text-slate-900 font-medium truncate max-w-[180px]">
										{inv.number || inv.id}
									</td>
									<td className="px-4 py-3 text-sm text-slate-700">
										{formatDate(inv.created)}
									</td>
									<td className="px-4 py-3 text-xs">
										<span
											className={classNames(
												"inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset",
												statusToClasses(inv.status),
											)}
										>
											{statusToLabel(inv.status)}
										</span>
									</td>
									{/* <td className="px-4 py-3 text-sm text-slate-900 font-medium">
										{formatAmount(
											inv.amount_paid || inv.amount_due,
											inv.currency,
										)}
									</td> */}
									<td className="px-4 py-3 text-right">
										{inv.invoice_pdf ? (
											<Button
												size="medium"
												ghost
												href={inv.invoice_pdf}
												target="_blank"
												rel="noopener noreferrer"
												iconPrefix={ArrowDownTrayIcon}
												download
											>
												Download PDF
											</Button>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
